import { useEffect, useMemo, useRef, useState } from 'react';
import { C, EASE_SPRING, ink, text } from '../lib/theme';
import type { Reason } from '../lib/types';

/** Cor por grupo de motivo, só com tokens do tema. Regras em cinza, como no resto do Hub. */
export const GRUPO_COR: Record<string, string> = {
  'Pagamento e cobrança': C.churn,
  'Bitrix24': C.blue,
  'Concorrência': C.violet,
  'Preço e valor': C.amber,
  'Uso e adoção': C.blueSoft,
  'Produto e técnica': C.accent,
  'Atendimento': C.accentDeep,
  'Negócio do cliente': C.green,
  'Parceiro': C.greenDeep,
  'Sem causa clara': ink(0.5),
  'Regras': ink(0.38),
};
export const corDoGrupo = (g: string | null | undefined) => GRUPO_COR[g ?? ''] ?? ink(0.45);
const grupoDe = (r: Reason) => r.grupo ?? (r.is_exception ? 'Regras' : 'Sem grupo');

/** Chips das tags, em ordem: a primeira (★) é a principal. */
export function ReasonChips({ ids, reasons, max = 3 }: { ids: number[]; reasons: Reason[]; max?: number }) {
  const byId = new Map(reasons.map((r) => [r.id, r]));
  const list = ids.map((id) => byId.get(id)).filter((r): r is Reason => !!r);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', minWidth: 0 }}>
      {shown.map((r, i) => {
        const cor = corDoGrupo(grupoDe(r));
        return (
          <span key={r.id} title={`${i === 0 ? 'Principal · ' : ''}${grupoDe(r)}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 220,
            fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
            background: `color-mix(in srgb, ${cor} 16%, transparent)`,
            border: `1px solid color-mix(in srgb, ${cor} 45%, transparent)`, color: C.fg,
          }}>
            {i === 0 && <span style={{ color: cor }}>★</span>}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
          </span>
        );
      })}
      {list.length > max && <span style={{ fontSize: 10.5, fontWeight: 700, color: text.muted }}>+{list.length - max}</span>}
    </span>
  );
}

/** Ordem fixa das categorias no seletor; Regras sempre por último, separada. */
const ORDEM_GRUPOS = [
  'Pagamento e cobrança', 'Preço e valor', 'Uso e adoção', 'Produto e técnica', 'Atendimento',
  'Concorrência', 'Negócio do cliente', 'Parceiro', 'Bitrix24', 'Sem causa clara',
];

/**
 * Seletor de motivos em tags, em categoria → subcategoria (2026-10-27).
 * Coluna da esquerda: categorias (grupo do motivo) com a cor e quantas tags
 * já estão marcadas nela. Direita: os motivos da categoria como tags
 * clicáveis (liga/desliga). Topo: a bandeja com as tags escolhidas, a
 * primeira (★) é a principal e é a única que entra nos KPIs via reason_id.
 * Regra é seleção única: escolher uma regra substitui as outras tags, e
 * escolher um motivo com uma regra marcada troca a regra pelo motivo.
 */
export function ReasonTagPicker({ reasons, value, onChange, onClose, align = 'right', inline = false }: {
  reasons: Reason[];
  value: number[];
  onChange: (picked: Reason[]) => void;
  onClose?: () => void;
  align?: 'left' | 'right';
  inline?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const byId = useMemo(() => new Map(reasons.map((r) => [r.id, r])), [reasons]);
  const picked = useMemo(() => value.map((id) => byId.get(id)).filter((r): r is Reason => !!r), [value, byId]);
  const temRegra = picked.some((r) => r.is_exception);

  const grupos = useMemo(() => {
    const m = new Map<string, Reason[]>();
    for (const r of reasons) {
      if (!r.active && !value.includes(r.id)) continue;
      const g = grupoDe(r);
      m.set(g, [...(m.get(g) ?? []), r]);
    }
    for (const rs of m.values()) rs.sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0) || a.label.localeCompare(b.label));
    const pos = (g: string) => (g === 'Regras' ? 999 : ORDEM_GRUPOS.indexOf(g) === -1 ? 500 : ORDEM_GRUPOS.indexOf(g));
    return [...m.entries()].sort(([a], [b]) => pos(a) - pos(b) || a.localeCompare(b));
  }, [reasons, value]);

  const inicial = picked[0] ? grupoDe(picked[0]) : grupos[0]?.[0] ?? '';
  const [ativo, setAtivo] = useState(inicial);
  const ativoValido = grupos.some(([g]) => g === ativo) ? ativo : grupos[0]?.[0] ?? '';

  useEffect(() => {
    if (inline || !onClose) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [onClose, inline]);

  const toggle = (r: Reason) => {
    if (value.includes(r.id)) { setHint(null); onChange(picked.filter((x) => x.id !== r.id)); return; }
    if (r.is_exception) {
      setHint(picked.length > 0 ? 'Regra é seleção única: as outras tags saíram.' : null);
      onChange([r]);
      return;
    }
    if (temRegra) { setHint('A regra saiu: regra não combina com outros motivos.'); onChange([r]); return; }
    setHint(null);
    onChange([...picked, r]);
  };
  const makePrimary = (id: number) => {
    const r = picked.find((x) => x.id === id);
    if (r) onChange([r, ...picked.filter((x) => x.id !== id)]);
  };

  const q = query.trim().toLowerCase();
  const busca = useMemo(() => (q
    ? grupos.map(([g, rs]) => [g, rs.filter((r) => r.label.toLowerCase().includes(q) || g.toLowerCase().includes(q))] as const)
        .filter(([, rs]) => rs.length > 0)
    : []), [grupos, q]);

  const tag = (r: Reason) => {
    const cor = corDoGrupo(grupoDe(r));
    const pos = value.indexOf(r.id);
    const on = pos !== -1;
    return (
      <button key={r.id} type="button" onClick={() => toggle(r)} aria-pressed={on}
        title={on ? (pos === 0 ? 'Motivo principal · clique para remover' : 'Clique para remover') : 'Clique para adicionar'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', font: 'inherit', textAlign: 'left',
          fontSize: 11.5, fontWeight: 650, lineHeight: 1.3, padding: '4px 9px', borderRadius: 99,
          color: on ? C.fg : ink(0.8),
          background: on ? `color-mix(in srgb, ${cor} 26%, transparent)` : ink(0.04),
          border: `1px solid ${on ? cor : ink(0.12)}`,
          transition: `background 0.15s, border-color 0.15s`,
        }}>
        <span style={{ fontSize: 11, color: on ? cor : ink(0.35), flex: 'none' }}>{on ? (pos === 0 ? '★' : '✓') : '+'}</span>
        {r.label}
      </button>
    );
  };

  const [gAtivo, rsAtivo] = grupos.find(([g]) => g === ativoValido) ?? ['', []];
  const corAtiva = corDoGrupo(gAtivo);

  return (
    <div
      ref={ref} onClick={(e) => e.stopPropagation()}
      style={inline ? { width: '100%' } : {
        position: 'absolute', top: 'calc(100% + 6px)', [align]: 0, zIndex: 75, width: 'min(590px, 92vw)',
        background: 'var(--color-bg-lift)', border: `1px solid ${ink(0.13)}`, borderRadius: 14,
        boxShadow: '0 22px 60px rgba(10,10,18,0.6)', padding: 10, textAlign: 'left',
        // Estilo macOS: cresce a partir do botão que abriu (canto superior do lado alinhado).
        transformOrigin: `top ${align}`, animation: `koPopScale 0.32s ${EASE_SPRING} both`,
      }}
    >
      {/* Bandeja: tags escolhidas */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', minHeight: 26, marginBottom: 4, padding: '0 2px', animation: 'koFadeUp 0.25s 0.08s both' }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: text.muted, marginRight: 2 }}>
          Motivos {picked.length > 0 && `· ${picked.length}`}
        </span>
        {picked.length === 0 && <span style={{ fontSize: 11.5, color: text.muted }}>Escolha a categoria e marque os motivos.</span>}
        {picked.map((r, i) => {
          const cor = corDoGrupo(grupoDe(r));
          return (
            <span key={r.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: C.fg,
              padding: '4px 5px 4px 9px', borderRadius: 99,
              background: `color-mix(in srgb, ${cor} 22%, transparent)`, border: `1px solid ${cor}`,
            }}>
              <button type="button" title={i === 0 ? 'Motivo principal (conta nos KPIs)' : 'Tornar principal'}
                onClick={() => makePrimary(r.id)}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: i === 0 ? cor : ink(0.4), fontSize: 12, lineHeight: 1 }}
              >{i === 0 ? '★' : '☆'}</button>
              <span style={{ color: ink(0.55), fontWeight: 600 }}>{grupoDe(r)} ›</span>
              {r.label}
              <button type="button" aria-label={`Remover ${r.label}`} onClick={() => toggle(r)}
                style={{ border: 'none', background: ink(0.1), color: ink(0.7), width: 16, height: 16, borderRadius: 99, cursor: 'pointer', fontSize: 10, lineHeight: '16px', padding: 0 }}
              >×</button>
            </span>
          );
        })}
      </div>
      {(hint || picked.length > 1) && (
        <div style={{ fontSize: 10.5, color: hint ? C.amber : text.muted, margin: '0 2px 6px', lineHeight: 1.4 }}>
          {hint ?? '★ é o principal: só ele entra nos KPIs. Clique em ☆ para trocar.'}
        </div>
      )}

      <input
        ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar em todas as categorias…"
        style={{
          width: '100%', boxSizing: 'border-box', border: `1px solid ${ink(0.14)}`, outline: 'none', borderRadius: 10,
          background: 'var(--input-bg)', color: C.fg, font: 'inherit', fontSize: 12, padding: '6px 10px', marginBottom: 8,
        }}
      />

      {q ? (
        <div style={{ maxHeight: 268, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {busca.length === 0 && <div style={{ fontSize: 12, color: text.muted, padding: 6 }}>Nenhum motivo com “{query}”.</div>}
          {busca.map(([g, rs]) => (
            <div key={g}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: corDoGrupo(g), marginBottom: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: corDoGrupo(g) }} />{g}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{rs.map(tag)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '232px minmax(0,1fr)', gap: 8, alignItems: 'start', animation: 'koFadeUp 0.25s 0.1s both' }}>
          {/* Categorias */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 268, overflow: 'auto', paddingRight: 2 }}>
            {grupos.map(([g, rs]) => {
              const cor = corDoGrupo(g);
              const on = g === ativoValido;
              const marcados = rs.filter((r) => value.includes(r.id)).length;
              return (
                <button key={g} type="button" onClick={() => setAtivo(g)} style={{
                  display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit',
                  fontSize: 11.5, fontWeight: on ? 700 : 600, color: on ? C.fg : ink(0.72), whiteSpace: 'nowrap',
                  padding: '6px 8px', borderRadius: 8, marginTop: g === 'Regras' ? 6 : 0, transition: 'background 0.15s, border-color 0.15s',
                  border: `1px solid ${on ? `color-mix(in srgb, ${cor} 55%, transparent)` : 'transparent'}`,
                  background: on ? `color-mix(in srgb, ${cor} 16%, transparent)` : 'transparent',
                }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: cor, flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={g}>{g}</span>
                  {marcados > 0
                    ? <span style={{ fontSize: 10.5, fontWeight: 800, color: C.fg, background: cor, borderRadius: 99, minWidth: 18, height: 18, lineHeight: '18px', textAlign: 'center', padding: '0 5px' }}>{marcados}</span>
                    : <span style={{ fontSize: 10.5, color: text.muted }}>{rs.length}</span>}
                </button>
              );
            })}
          </div>
          {/* Subcategorias da categoria ativa */}
          <div style={{
            borderRadius: 10, padding: 10, minWidth: 0, maxHeight: 268, overflow: 'auto',
            background: `color-mix(in srgb, ${corAtiva} 6%, transparent)`, border: `1px solid color-mix(in srgb, ${corAtiva} 25%, transparent)`,
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: corAtiva }}>{gAtivo}</span>
              {gAtivo === 'Regras' && <span style={{ fontSize: 10.5, color: text.muted }}>seleção única · não conta como perda</span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{rsAtivo.map(tag)}</div>
          </div>
        </div>
      )}

      {!inline && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
          <button type="button" disabled={picked.length === 0} onClick={() => { setHint(null); onChange([]); }} style={{
            marginRight: 'auto', border: 'none', background: 'transparent', color: ink(picked.length ? 0.55 : 0.25),
            cursor: picked.length ? 'pointer' : 'default', font: 'inherit', fontSize: 11.5, fontWeight: 600, padding: '6px 4px',
          }}>Limpar motivos</button>
          <button type="button" onClick={onClose} style={{
            border: 'none', background: C.accent, color: '#fff', cursor: 'pointer',
            font: 'inherit', fontSize: 11.5, fontWeight: 700, padding: '6px 16px', borderRadius: 8,
          }}>Concluir</button>
        </div>
      )}
    </div>
  );
}
